import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api.js';
import { BrandMark } from '../components/BrandMark.js';
import { askProductAdmin } from '../lib/brand.js';

function friendly(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'reset_invalid':
        return 'This reset link is invalid, has expired, or was already used. Request a new one.';
      case 'too_many_attempts':
        return 'Too many attempts. Wait a few minutes and try again.';
      case 'weak_password':
        return err.message;
      case 'invalid_body':
        return 'Please fill in all fields.';
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

function Card({ title, sub, children }: { title: string; sub: React.ReactNode; children: React.ReactNode }): JSX.Element {
  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-card__head">
          <div className="auth-card__mark"><BrandMark size="lg" /></div>
          <h1 className="auth-card__title">{title}</h1>
          <p className="auth-card__sub">{sub}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

/** "Forgot password?" → enter email → we send a one-time link (valid 1 hour). */
export function ForgotPassword({
  initialEmail,
  emailEnabled,
  onBack,
}: {
  initialEmail: string;
  emailEnabled: boolean;
  onBack: () => void;
}): JSX.Element {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(emailEnabled);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.requestPasswordReset(email.trim());
      setEnabled(r.emailEnabled);
      if (r.emailEnabled) setSentTo(email.trim());
    } catch (err) {
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  }

  const back = (
    <div className="auth-foot auth-foot--center">
      <button type="button" className="btn btn--tertiary btn--sm" onClick={onBack}>
        Back to sign in
      </button>
    </div>
  );

  if (!enabled) {
    return (
      <Card title="Reset your password" sub="Email reset is not set up yet.">
        <div className="form-note" role="status">
          {askProductAdmin('for a set-password link.')} They can create one under Admin by
          opening your account and choosing Create set-password link. The link lets you choose a
          new password.
        </div>
        {back}
      </Card>
    );
  }

  if (sentTo) {
    return (
      <Card title="Check your email" sub="We sent you a reset link.">
        <div className="form-success" role="status">
          If an account exists for <strong>{sentTo}</strong>, an email with a link to reset your password is
          on its way. The link works once and expires in 1 hour.
        </div>
        <p className="form-help" style={{ margin: 0 }}>
          Nothing after a few minutes? Check spam, or{' '}
          <button type="button" className="auth-link" onClick={() => setSentTo(null)}>try again</button>.
        </p>
        {back}
      </Card>
    );
  }

  return (
    <Card title="Reset your password" sub="Enter your email and we'll send you a reset link.">
      {error && <div className="form-error" role="alert">{error}</div>}
      <form className="auth-form" onSubmit={(e) => void submit(e)}>
        <div className="form-row">
          <label className="form-label" htmlFor="resetEmail">Email address</label>
          <input
            id="resetEmail"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            autoFocus
            required
          />
        </div>
        <button type="submit" className="btn" disabled={busy || !email.trim()}>
          {busy ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
      {back}
    </Card>
  );
}

/** Landing page for /login?reset=<token>: choose a new password, then signed in. */
export function ResetPassword({
  resetToken,
  onSignedIn,
  onCancel,
}: {
  resetToken: string;
  onSignedIn: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [email, setEmail] = useState<string | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.checkPasswordReset(resetToken).then(
      (r) => setEmail(r.email),
      (err) => setCheckError(friendly(err)),
    );
  }, [resetToken]);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.completePasswordReset(resetToken, password);
      onSignedIn();
    } catch (err) {
      const msg = friendly(err);
      if (err instanceof ApiError && err.code === 'reset_invalid') {
        setEmail(null);
        setCheckError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Choose a new password"
      sub={email ? <>For <strong>{email}</strong></> : checkError ? 'Reset link problem' : 'Checking your link…'}
    >
      {checkError && <div className="form-error" role="alert">{checkError}</div>}
      {error && <div className="form-error" role="alert">{error}</div>}
      {email && (
        <form className="auth-form" onSubmit={(e) => void submit(e)}>
          <input type="email" autoComplete="username" value={email} readOnly hidden />
          <div className="form-row">
            <label className="form-label" htmlFor="resetPassword">New password</label>
            <input
              id="resetPassword"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={10}
              required
              autoFocus
            />
            <span className="form-help">At least 10 characters. This signs you out on other devices.</span>
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="resetConfirm">Confirm new password</label>
            <input
              id="resetConfirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn" disabled={busy || !password || !confirm}>
            {busy ? 'Saving…' : 'Save password and sign in'}
          </button>
        </form>
      )}
      <div className="auth-foot auth-foot--center">
        <button type="button" className="btn btn--tertiary btn--sm" onClick={onCancel}>
          {checkError ? 'Back to sign in to request a new link' : 'Back to sign in'}
        </button>
      </div>
    </Card>
  );
}
